import type { FC } from "react"
import React, { useState } from "react"
import {
  Button,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Stack,
  useDisclosure,
} from "@chakra-ui/react"
import type { FastFieldProps } from "formik"
import { FastField, useFormikContext } from "formik"

import ImportDetailsModal from "createProposal/components/ImportDetailsModal"
import CreateProposalFormError from "createProposal/components/CreateProposalFormError"
import MarkdownEditor from "createProposal/components/MarkdownEditor"
import type {
  CreateProposalFormState,
  CreateProposalFormValues,
} from "createProposal/types/form"
import { StepName } from "createProposal/types/form"
import CardContainer from "common/components/CardContainer"
import { FileImport } from "ui/components/icons/font-awesome/FileImport"

type CreateProposalDetailsProps = {
  state: Pick<CreateProposalFormState, "currentStep">
}

const CreateProposalDetails: FC<CreateProposalDetailsProps> = ({ state }) => {
  const { currentStep } = state
  const isActive = currentStep === StepName.DETAILS

  const [displayMarkdownEditor, setDisplayMarkdownEditor] = useState(true)

  const importDetailsModal = useDisclosure()

  const { values, setFieldTouched } =
    useFormikContext<CreateProposalFormValues>()

  return (
    <>
      <CardContainer
        display={isActive ? "" : "none"}
        mt={0}
        p={0}
        pt={{ base: 6, sm: 2 }}
        width="100%"
      >
        <Stack p={2} spacing={4} w="full">
          <Stack direction="row" justify="end">
            <Button
              leftIcon={<Icon as={FileImport} />}
              variant="secondary"
              onClick={() => importDetailsModal.onOpen()}
            >
              Import
            </Button>
          </Stack>
          <FastField name="details.title">
            {({ field, meta }: FastFieldProps) => {
              const isInvalid = Boolean(meta.error) && meta.touched

              const handleBlur = field.onBlur
              const handleChange = field.onChange

              return (
                <FormControl isInvalid={isInvalid}>
                  <FormLabel fontWeight="semibold">Title</FormLabel>
                  <Input
                    name={field.name}
                    placeholder="Enter the title of your proposal"
                    value={field.value}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  <CreateProposalFormError error={meta.error} />
                </FormControl>
              )
            }}
          </FastField>
          {displayMarkdownEditor ? (
            <FastField name="details.description">
              {({ form, meta }: FastFieldProps) => {
                const isInvalid = Boolean(meta.error && meta.touched)

                const handleChangeDescription = (
                  description?: string | undefined,
                ) => {
                  form.setFieldValue("details.description", description || "")
                }

                return (
                  <FormControl isInvalid={isInvalid}>
                    <FormLabel fontWeight="semibold">Description</FormLabel>
                    <MarkdownEditor
                      isInvalid={isInvalid}
                      placeholder="Enter your proposal description"
                      value={values.details.description}
                      onBlur={() => setFieldTouched("details.description")}
                      onChange={handleChangeDescription}
                    />
                    <CreateProposalFormError error={meta.error} />
                  </FormControl>
                )
              }}
            </FastField>
          ) : null}
          <FastField name="details.snapshotURL">
            {({ field, meta }: FastFieldProps) => {
              const isInvalid = Boolean(meta.error) && meta.touched

              const handleBlur = field.onBlur
              const handleChange = field.onChange

              return (
                <FormControl isInvalid={isInvalid}>
                  <FormLabel fontWeight="semibold">Snapshot URL</FormLabel>
                  <Input
                    name={field.name}
                    placeholder="https://snapshot.org/#/mydao.eth/"
                    value={field.value}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  <CreateProposalFormError error={meta.error} />
                </FormControl>
              )
            }}
          </FastField>
          <FastField name="details.discourseURL">
            {({ field, meta }: FastFieldProps) => {
              const isInvalid = Boolean(meta.error) && meta.touched

              const handleBlur = field.onBlur
              const handleChange = field.onChange

              return (
                <FormControl isInvalid={isInvalid}>
                  <FormLabel fontWeight="semibold">Discourse URL</FormLabel>
                  <Input
                    name={field.name}
                    placeholder="https://forum.mydao.com/t/"
                    value={field.value}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  <CreateProposalFormError error={meta.error} />
                </FormControl>
              )
            }}
          </FastField>
        </Stack>
      </CardContainer>
      <ImportDetailsModal
        isOpen={importDetailsModal.isOpen}
        setDisplayMarkdownEditor={setDisplayMarkdownEditor}
        onClose={importDetailsModal.onClose}
      />
    </>
  )
}

export default CreateProposalDetails
